import { AttioConsultantCacheData, AttioDealCacheData, AttioEngagementManagerCacheData, AttioValuationAttributes } from '../attio.types';
import { clientWithAuth } from '../base';

/**
 * 
 * Service for bulk loading Attio data
 */
export class AttioBulkService {
  private readonly client = clientWithAuth;
  private readonly baseUrl = '/api/attio/bulk';

  /**
   * Bulk load consultant data
   * @param consultantIds - Array of consultant record IDs
   * @returns Map of consultant ID to consultant data
   */
  async loadBulkConsultants(
    consultantIds: string[],
    signal?: AbortSignal
  ): Promise<Map<string, AttioConsultantCacheData>> {
    try {
      if (consultantIds.length === 0) {
        return new Map();
      }

      const response = await this.client.post(
        `${this.baseUrl}/consultants`,
        { consultant_ids: consultantIds },
        { signal }
      );

      // Convert response object to Map
      const resultMap = new Map<string, AttioConsultantCacheData>();
      if (response.data && typeof response.data === 'object') {
        Object.entries(response.data).forEach(([id, data]: [string, any]) => {
          resultMap.set(id, {
            name: data.name || '-',
            imageSrc: data.imageSrc || '-',
          });
        });
      }

      return resultMap;
    } catch (error) {
      console.error('Error in loadBulkConsultants:', error);
      // Return empty map on error
      return new Map();
    }
  }

  /**
   * Bulk load engagement manager data
   * @param managerIds - Array of engagement manager record IDs
   * @returns Map of manager ID to manager data
   */
  async loadBulkEngagementManagers(
    managerIds: string[],
    signal?: AbortSignal
  ): Promise<Map<string, AttioEngagementManagerCacheData>> {
    try {
      if (managerIds.length === 0) {
        return new Map();
      }

      const response = await this.client.post(
        `${this.baseUrl}/engagement-managers`,
        { manager_ids: managerIds },
        { signal }
      );

      // Convert response object to Map
      const resultMap = new Map<string, AttioEngagementManagerCacheData>();
      if (response.data && typeof response.data === 'object') {
        Object.entries(response.data).forEach(([id, data]: [string, any]) => {
          resultMap.set(id, {
            name: data.name || '-',
            imageSrc: data.imageSrc || '-',
          });
        });
      }

      return resultMap;
    } catch (error) {
      console.error('Error in loadBulkEngagementManagers:', error);
      // Return empty map on error
      return new Map();
    }
  }

  /**
   * Bulk load deal data
   * @param dealIds - Array of deal record IDs
   * @returns Map of deal ID to deal data
   */
  async loadBulkDeals(
    dealIds: string[],
    signal?: AbortSignal
  ): Promise<Map<string, AttioDealCacheData>> {
    try {
      if (dealIds.length === 0) {
        return new Map();
      }

      const response = await this.client.post(
        `${this.baseUrl}/deals`,
        { deal_ids: dealIds },
        { signal }
      );

      // Convert response object to Map
      const resultMap = new Map<string, AttioDealCacheData>();
      if (response.data && typeof response.data === 'object') {
        Object.entries(response.data).forEach(([id, data]: [string, any]) => {
          resultMap.set(id, {
            value: data.value || '-',
            due: data.due || '-',
            stage: data.stage || '-',
          });
        });
      }

      return resultMap;
    } catch (error) {
      console.error('Error in loadBulkDeals:', error);
      // Return empty map on error
      return new Map();
    }
  }

  /**
   * Bulk load valuation data
   * @param valuationIds - Array of valuation record IDs
   * @returns Map of valuation ID to valuation data
   */
  async loadBulkValuations(
    valuationIds: string[],
    signal?: AbortSignal
  ): Promise<Map<string, AttioValuationAttributes>> {
    try {
      if (valuationIds.length === 0) {
        return new Map();
      }

      const response = await this.client.post(
        `${this.baseUrl}/valuations`,
        { valuation_ids: valuationIds },
        { signal }
      );
      const resultMap = new Map<string, AttioValuationAttributes>();
      if (response.data && typeof response.data === 'object') {
        Object.entries(response.data).forEach(([id, data]: [string, any]) => {
          resultMap.set(id, {
            company_name: data.company_name || '-',
            status: data.stage || '-',
            valuation_date: data.valuation_date || '-',
            valuation_fee: data.valuation_fee || '-',
            note: data.note || '-',
            engagement_manager: data.engagement_manager || '-',
            engagement_manager_image_src: data.engagement_manager_image_src || '-',
            record_id: data.record_id || '-',
            created_at: data.created_at || '-',
            deal: data.deal || '-',
            deal_due: data.deal_due || '-',
            deal_stage: data.deal_stage || '-',
            other_consultant: data.other_consultant || '-',
            other_consultant_image_src: data.other_consultant_image_src || '-',
            other_consultant_id: data.other_consultant_id || '-',
            deal_id: data.deal_id || '-',
            engagement_manager_id: data.engagement_manager_id || '-',
            is_loading_additional_data: data.is_loading_additional_data || '-',
            net_value: data.net_value || '-',
            split: data.split || '-',
            payment_1: data.payment_1 || '-',
            pay_1_date: data.pay_1_date || '-',
            payment_2: data.payment_2 || '-',
            pay_2_date: data.pay_2_date || '-',
            payment_3: data.payment_3 || '-',
            pay_3_date: data.pay_3_date || '-',
            refund: data.refund || '-',
            refund_date: data.refund_date || '-',
            due: data.due || '-',
            adjustment: data.adjustment || '-',
            adjustment_date: data.adjustment_date || '-',
            bad_debt: data.bad_debt || '-',
            bad_debt_date: data.bad_debt_date || '-',
            renewal_rate: data.renewal_rate || '-',

            engagement_type: data.engagement_type || undefined,
            valuation_lead_94: data.valuation_lead_94 || undefined,
            stage: data.stage || undefined,
          } as unknown as AttioValuationAttributes);
        });
      }
      return resultMap;
    } catch (error) {
      // AbortError / CanceledError are expected when requests are intentionally cancelled
      const isCancel =
        (error as { code?: string })?.code === 'ERR_CANCELED' ||
        (error instanceof Error && error.name === 'AbortError');
      if (!isCancel) {
        console.error('Error in loadBulkValuations:', error);
      }
      return new Map();
    }
  }

  /**
   * Bulk load all related data at once
   * @param consultantIds - Array of consultant record IDs
   * @param managerIds - Array of engagement manager record IDs
   * @param dealIds - Array of deal record IDs
   * @returns Object containing all three maps
   */
  async loadAllBulkData(
    consultantIds: string[],
    managerIds: string[],
    dealIds: string[],
    signal?: AbortSignal
  ): Promise<{
    consultants: Map<string, AttioConsultantCacheData>;
    managers: Map<string, AttioEngagementManagerCacheData>;
    deals: Map<string, AttioDealCacheData>;
  }> {
    try {
      // Load all three types in parallel
      const [consultants, managers, deals] = await Promise.all([
        this.loadBulkConsultants(consultantIds, signal),
        this.loadBulkEngagementManagers(managerIds, signal),
        this.loadBulkDeals(dealIds, signal),
      ]);

      return { consultants, managers, deals };
    } catch (error) {
      console.error('Error in loadAllBulkData:', error);
      // Return empty maps on error
      return {
        consultants: new Map(),
        managers: new Map(),
        deals: new Map(),
      };
    }
  }
}

